import RadioButton from 'flow/components/ui-components/RadioButton';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from '../../components/ReferralNumberCombobox';
import SettingSection from '../../SettingSection';
import onUpdate from '../functions/onUpdate';

interface Params {
    profileKey: ProfileKey | 'profile';
    extensionKey: ExtensionKey;
}

const withCallSettings =
    (
        callType: 'busy' | 'delayed' | 'disconnect' | 'landline',
        profileSetting: string,
        defaultRedirectType: string,
    ) =>
    ({ profile }: { profile: TypedMap<Profile> }) => {
        const { i18next } = useAppContext();
        const dispatch = useAppDispatch();
        const { profileKey, extensionKey } = useParams<Params>();
        const { profiles } = useProfiles(extensionKey);
        const [selectedRedirectType, setSelectedRedirectType] = useState(
            profile.getIn(['callForwarding', defaultRedirectType, 'type']),
        );

        const [selectedButton, setSelectedButton] = useState<
            'busy' | 'number' | 'voicemail' | 'sharedvoicemail'
        >(selectedRedirectType);

        const [referralNumber, setReferralNumber] =
            useState<ReferralNumberOption>({
                dto: {},
                label: null,
            });

        useEffect(() => {
            setReferralNumber({
                label: profile.getIn([
                    profileSetting,
                    defaultRedirectType,
                    'forwardingNumber',
                    'number',
                ]),
                dto: {},
            });
        }, []);

        const handleRadioChange = (type: 'busy' | 'voicemail') => {
            setSelectedRedirectType(type);
            setSelectedButton(type);
            dispatch(
                onUpdate(
                    extensionKey,
                    profileKey,
                    [
                        {
                            state: [
                                profileSetting,
                                defaultRedirectType,
                                'type',
                            ],
                            value: type,
                        },
                    ],
                    profiles,
                ),
            );
        };

        return (
            <>
                <SettingSection>
                    {i18next.t('settings.title.logic')}
                </SettingSection>

                <RadioButton
                    name={callType}
                    checked={selectedButton === 'busy'}
                    onChange={() => handleRadioChange('busy')}
                >
                    {i18next.t(`settings.incoming_calls.${callType}.busy`)}
                </RadioButton>

                <RadioButton
                    name={callType}
                    checked={selectedButton === 'voicemail'}
                    onChange={() => handleRadioChange('voicemail')}
                >
                    {i18next.t(`settings.incoming_calls.${callType}.voicemail`)}
                </RadioButton>

                <RadioButton
                    name={callType}
                    checked={['number', 'sharedvoicemail'].includes(
                        selectedButton,
                    )}
                    onChange={() => {
                        setSelectedRedirectType('number');
                        setSelectedButton('number');
                    }}
                >
                    {i18next.t(`settings.incoming_calls.${callType}.number`)}
                </RadioButton>

                {['number', 'sharedvoicemail'].includes(selectedButton) && (
                    <ReferralNumberCombobox
                        ariaLabel={i18next.t(
                            'settings.callrouting.action.picknumber',
                        )}
                        referralNumber={referralNumber}
                        onChange={(option) => {
                            setReferralNumber(option);
                            dispatch(
                                onUpdate(
                                    extensionKey,
                                    profileKey,
                                    [
                                        {
                                            state: [
                                                profileSetting,
                                                defaultRedirectType,
                                                'forwardingNumber',
                                            ],
                                            value: option?.dto,
                                        },
                                        {
                                            state: [
                                                profileSetting,
                                                defaultRedirectType,
                                                'type',
                                            ],
                                            value: 'number',
                                        },
                                    ],
                                    profiles,
                                ),
                            );
                        }}
                    />
                )}
            </>
        );
    };

const Busy = withCallSettings('busy', 'callForwarding', 'busyRedirectNumber');
const Delay = withCallSettings(
    'delayed',
    'callForwarding',
    'delayedRedirectNumber',
);
const Disconnect = withCallSettings(
    'delayed',
    'callForwarding',
    'errorRedirectNumber',
);

export { Busy, Delay, Disconnect };
